import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/shared/store/store';
import { sessionService } from '@/shared/services/session.service';
import {
  clearAuth,
  initializeAuth,
} from '@/features/security/sagas/auth-slice';
import { clearAuthTokens } from '@/config/axios.config';

/**
 * Hook centralizado para manejo de sesiones
 * Proporciona una interfaz unificada para todas las operaciones de sesión
 */
export const useSession = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, token, refreshToken, isAuthenticated, loading, error } =
    useSelector((state: RootState) => state.auth);

  /**
   * Inicializa la sesión desde las cookies
   */
  const initializeSession = useCallback(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  /**
   * Verifica si hay una sesión activa
   */
  const hasActiveSession = useCallback(() => {
    return sessionService.hasActiveSession();
  }, []);

  /**
   * Obtiene el token actual
   */
  const getCurrentToken = useCallback(() => {
    return null;
  }, []);

  /**
   * Obtiene el refresh token actual
   */
  const getCurrentRefreshToken = useCallback(() => {
    return null;
  }, []);

  /**
   * Obtiene el usuario actual
   */
  const getCurrentUser = useCallback(() => {
    return sessionService.getUser();
  }, []);

  /**
   * Obtiene la URL de redirección
   */
  const getRedirectUrl = useCallback(() => {
    return sessionService.getRedirectUrl();
  }, []);

  /**
   * Limpia la sesión completamente
   */
  const clearSession = useCallback(() => {
    sessionService.clearSession();
    clearAuthTokens();
    dispatch(clearAuth());
  }, [dispatch]);

  /**
   * Actualiza el token en la sesión (solo para compatibilidad)
   * Con HTTP-only cookies, esto no hace nada
   */
  const updateSessionToken = useCallback((_newToken: string) => {
    // No-op: tokens se manejan con HTTP-only cookies
  }, []);

  /**
   * Actualiza los tokens en la sesión (solo para compatibilidad)
   * Con HTTP-only cookies, esto no hace nada
   */
  const updateSessionTokens = useCallback(
    (_newToken: string, _newRefreshToken: string) => {
      // No-op: tokens se manejan con HTTP-only cookies
    },
    []
  );

  /**
   * Obtiene información de debug de la sesión
   */
  const getSessionDebugInfo = useCallback(() => {
    return {
      ...sessionService.getDebugInfo(),
      reduxState: {
        isAuthenticated,
        hasUser: !!user,
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        loading,
        error,
      },
    };
  }, [isAuthenticated, user, token, refreshToken, loading, error]);

  /**
   * Verifica si el usuario tiene un rol específico
   */
  const hasRole = useCallback(
    (role: string) => {
      return user?.role === role;
    },
    [user]
  );

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   */
  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return user?.role ? roles.includes(user.role) : false;
    },
    [user]
  );

  /**
   * Verifica si el usuario es administrador
   */
  const isAdmin = useCallback(() => {
    return user?.role === 'SUPER_ADMIN';
  }, [user]);

  /**
   * Verifica si el usuario es un administrador de negocio
   */
  const isBusinessAdmin = useCallback(() => {
    return user?.role
      ? user.role.endsWith('_ADMIN') && user.role !== 'SUPER_ADMIN'
      : false;
  }, [user]);

  return {
    // Estado de la sesión
    user,
    token,
    refreshToken,
    isAuthenticated,
    loading,
    error,

    // Operaciones de sesión
    initializeSession,
    hasActiveSession,
    clearSession,
    updateSessionToken,
    updateSessionTokens,

    // Getters
    getCurrentToken,
    getCurrentRefreshToken,
    getCurrentUser,
    getRedirectUrl,

    // Verificaciones de rol
    hasRole,
    hasAnyRole,
    isAdmin,
    isBusinessAdmin,

    // Debug
    getSessionDebugInfo,
  };
};

export default useSession;
